/* eslint-disable max-lines-per-function */

import { AlertTriangle, CheckCircle2, Image as ImageIcon, Upload, XCircle } from "lucide-react";
import { useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { Checkbox } from "../../../../shared/components/ui/checkbox";
import { TableCell, TableRow } from "../../../../shared/components/ui/table";
import { useImageUpload } from "../../../content-editing/hooks/useImageUpload";
import {
  extractOriginalFilenames,
  extractValidationWarnings,
} from "../../../content-editing/utils/image-validation.utils";
import type { BulkEditTitle } from "../types";
import { EditImageAssetModal } from "./EditImageAssetModal";
import { TitleActionsDropdown } from "./TitleActionsDropdown";

interface VisualAssetsRowProps {
  readonly title: BulkEditTitle;
  readonly isSelected: boolean;
  readonly onSelect: (titleId: string, selected: boolean) => void;
  readonly onFieldUpdate: (
    titleId: string,
    field: string,
    value: string | number | boolean | string[] | null,
  ) => Promise<void>;
  readonly onDelete: (titleId: string, titleName: string) => void;
  readonly onDuplicate: (titleId: string) => void;
  readonly onRefresh?: (titleId: string) => Promise<void>;
}

type ImageAssetType = "poster" | "backdrop" | "logo" | "horizontalTile" | "verticalTile";

// Map asset types to API imageType values
const imageTypeMap: Record<ImageAssetType, string> = {
  poster: "poster",
  backdrop: "backdrop",
  logo: "logo",
  horizontalTile: "horizontal-tile",
  verticalTile: "vertical-tile",
};

interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Single row in the Visual Assets view table
 * Shows: Title, Poster, Backdrop, Logo, H-Tile, V-Tile, Source, Status
 */
export function VisualAssetsRow({
  title,
  isSelected,
  onSelect,
  onFieldUpdate,
  onDelete,
  onDuplicate,
  onRefresh,
}: Readonly<VisualAssetsRowProps>): ReactElement {
  const [activeModal, setActiveModal] = useState<ImageAssetType | null>(null);
  const { uploadImage } = useImageUpload();

  // Extract validation warnings from imageAssets metadata using shared utilities
  // Then map from hyphenated format to camelCase format used in bulk-edit
  const apiWarnings = extractValidationWarnings(title.imageAssets);
  const apiFileNames = extractOriginalFilenames(title.imageAssets);

  // Map to camelCase keys for bulk-edit compatibility
  const validationWarnings: Partial<Record<ImageAssetType, string[]>> = {
    poster: apiWarnings.poster,
    backdrop: apiWarnings.backdrop,
    logo: apiWarnings.logo,
    horizontalTile: apiWarnings["horizontal-tile"],
    verticalTile: apiWarnings["vertical-tile"],
  };

  const originalFileNames: Partial<Record<ImageAssetType, string>> = {
    poster: apiFileNames.poster,
    backdrop: apiFileNames.backdrop,
    logo: apiFileNames.logo,
    horizontalTile: apiFileNames["horizontal-tile"],
    verticalTile: apiFileNames["vertical-tile"],
  };

  const imageAssets: Array<{
    type: ImageAssetType;
    label: string;
    url: string | null;
    field: string;
  }> = [
    { type: "poster", label: "Poster", url: title.posterUrlHd, field: "posterUrlHd" },
    { type: "backdrop", label: "Backdrop", url: title.backdropUrlHd, field: "backdropUrlHd" },
    { type: "logo", label: "Logo", url: title.logoUrl, field: "logoUrl" },
    { type: "horizontalTile", label: "H-Tile", url: title.horizontalTileUrl, field: "horizontalTileUrl" },
    { type: "verticalTile", label: "V-Tile", url: title.verticalTileUrl, field: "verticalTileUrl" },
  ];

  // Count how many images are present
  const imageCount = imageAssets.filter((asset) => Boolean(asset.url)).length;
  const allImagesPresent = imageCount === 5;
  const someImagesMissing = imageCount > 0 && imageCount < 5;

  // Determine primary source (majority of images)
  const sources = [title.posterSource, title.backdropSource].filter(Boolean);
  const tmdbCount = sources.filter((s) => s === "TMDB").length;
  const r2Count = sources.filter((s) => s === "R2").length;

  let primarySource: string;
  if (tmdbCount > 0 && r2Count > 0) {
    primarySource = `T:${tmdbCount} R:${r2Count}`;
  } else if (tmdbCount > r2Count) {
    primarySource = "TMDB";
  } else if (r2Count > tmdbCount) {
    primarySource = "R2";
  } else {
    primarySource = "—";
  }

  const handleUpload = async (
    assetType: ImageAssetType,
    file: File,
    onValidationResult?: (result: ValidationResult) => void,
  ): Promise<void> => {
    const asset = imageAssets.find((a) => a.type === assetType);
    if (!asset) return;

    try {
      // Use the shared useImageUpload hook which handles everything:
      // - Presigned URL generation
      // - R2 upload
      // - Metadata extraction and validation
      // - Returns validation results and enriched metadata
      const result = await uploadImage({
        titleId: title.id,
        file,
        imageType: imageTypeMap[assetType] as "poster" | "backdrop" | "logo" | "horizontal-tile" | "vertical-tile",
        mode: "title",
      });

      // Pass validation results to modal for display
      if (result.validation && onValidationResult) {
        onValidationResult(result.validation);
      }

      // Update title in database with R2 key
      const r2Url = `r2://${result.key}`;
      await onFieldUpdate(title.id, asset.field, r2Url);

      // Refresh this title's data to get presigned URLs and updated validation metadata
      if (onRefresh) {
        await onRefresh(title.id);
      }
    } catch (err) {
      console.error("Failed to upload image:", err);
      throw err;
    }
  };

  const handleDelete = async (assetType: ImageAssetType): Promise<void> => {
    const asset = imageAssets.find((a) => a.type === assetType);
    if (!asset) return;

    // Set field to null to remove the image
    await onFieldUpdate(title.id, asset.field, null);
  };

  const renderImageCell = (asset: { type: ImageAssetType; label: string; url: string | null }) => {
    const hasImage = Boolean(asset.url);
    const warnings = validationWarnings[asset.type];
    const hasWarnings = warnings && warnings.length > 0;

    return (
      <button
        type="button"
        onClick={() => {
          setActiveModal(asset.type);
        }}
        className="flex items-center justify-center gap-1.5 text-xs hover:text-lumi-accent transition-colors w-full relative group"
      >
        {hasImage ? (
          <>
            <ImageIcon className="h-4 w-4 text-neutral-400" />
            <CheckCircle2 className={`h-3 w-3 ${hasWarnings ? "text-yellow-500" : "text-green-500"}`} />
          </>
        ) : (
          <>
            <ImageIcon className="h-4 w-4 text-neutral-600" />
            <XCircle className="h-3 w-3 text-neutral-600" />
          </>
        )}
        {hasWarnings && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-all duration-100 delay-300 z-50 pointer-events-none">
            <div className="bg-neutral-900 border border-yellow-700/30 rounded px-3 py-2 text-xs text-yellow-300 whitespace-nowrap shadow-lg flex items-start gap-1">
              <AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" />
              <span>{warnings.join("; ")}</span>
            </div>
          </div>
        )}
      </button>
    );
  };

  return (
    <>
      <TableRow className="hover:bg-neutral-900/30">
        <TableCell className="w-12">
          <Checkbox checked={isSelected} onCheckedChange={(checked) => onSelect(title.id, checked === true)} />
        </TableCell>
        <TableCell>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-white truncate">{title.name}</span>
          </div>
        </TableCell>
        <TableCell className="text-center">{renderImageCell(imageAssets[0])}</TableCell>
        <TableCell className="text-center">{renderImageCell(imageAssets[1])}</TableCell>
        <TableCell className="text-center">{renderImageCell(imageAssets[2])}</TableCell>
        <TableCell className="text-center">{renderImageCell(imageAssets[3])}</TableCell>
        <TableCell className="text-center">{renderImageCell(imageAssets[4])}</TableCell>
        <TableCell>
          <span className="text-xs text-neutral-400">{primarySource}</span>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1.5">
            {allImagesPresent && <CheckCircle2 className="h-4 w-4 text-green-400" />}
            {someImagesMissing && <Upload className="h-4 w-4 text-yellow-400" />}
            {!allImagesPresent && !someImagesMissing && <XCircle className="h-4 w-4 text-red-400" />}
            <span className="text-xs text-neutral-400">
              {allImagesPresent && "Complete"}
              {someImagesMissing && "Partial"}
              {!allImagesPresent && !someImagesMissing && "Missing"}
            </span>
          </div>
        </TableCell>
        <TableCell className="text-center">
          <TitleActionsDropdown
            titleId={title.id}
            titleName={title.name}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
          />
        </TableCell>
      </TableRow>

      {/* Image Asset Modals */}
      {imageAssets.map((asset) =>
        activeModal === asset.type
          ? createPortal(
              <EditImageAssetModal
                open={true}
                onClose={() => {
                  setActiveModal(null);
                }}
                titleName={title.name}
                imageType={asset.label}
                imageUrl={asset.url}
                originalFileName={originalFileNames[asset.type]}
                persistedWarnings={validationWarnings[asset.type]}
                onUpload={(file: File, onValidation?: (result: ValidationResult) => void) =>
                  handleUpload(asset.type, file, onValidation)
                }
                onDelete={() => handleDelete(asset.type)}
              />,
              document.body,
            )
          : null,
      )}
    </>
  );
}
